/**
 * @file sheet.tsx
 * @description Side-sheet (Drawer) для mobile-nav и slide-over panels (phase08.5).
 * Adaptated under base-ui dialog with side variants.
 */

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-[var(--z-overlay)] bg-on-surface/30 backdrop-blur-sm",
        "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
        "transition-opacity duration-200",
        className,
      )}
      {...props}
    />
  );
}

const sheetVariants = cva(
  "fixed z-[var(--z-dialog)] bg-surface-lowest p-6 shadow-lg transition-all duration-300 flex flex-col gap-4",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b border-outline-variant data-[ending-style]:-translate-y-full data-[starting-style]:-translate-y-full",
        bottom:
          "inset-x-0 bottom-0 border-t border-outline-variant data-[ending-style]:translate-y-full data-[starting-style]:translate-y-full",
        left: "inset-y-0 left-0 h-full w-3/4 max-w-sm data-[ending-style]:-translate-x-full data-[starting-style]:-translate-x-full",
        right:
          "inset-y-0 right-0 h-full w-3/4 max-w-sm data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full",
      },
    },
    defaultVariants: { side: "right" },
  },
);

interface SheetContentProps
  extends React.ComponentProps<typeof DialogPrimitive.Popup>,
    VariantProps<typeof sheetVariants> {
  showClose?: boolean;
}

function SheetContent({
  side,
  className,
  children,
  showClose = true,
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Popup
        data-slot="sheet-content"
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
        {showClose ? (
          // Область нажатия 48×48 (тач-цель на телефоне, phase19.31), а значок
          // и его подложка при наведении — прежние: центр значка остаётся в
          // 28px от верхнего и правого края, как при `top-4 right-4 p-1`.
          <DialogPrimitive.Close
            data-slot="sheet-close"
            className="group/sheet-close absolute right-1 top-1 inline-flex size-12 items-center justify-center rounded-md text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="rounded-md p-1 transition-colors group-hover/sheet-close:bg-surface-low group-hover/sheet-close:text-on-surface">
              <X className="size-4" />
            </span>
            <span className="sr-only">Закрыть</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-1 pr-8", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn("text-lg font-semibold tracking-tight", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-on-surface-variant", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
